<template>
  <div class="flex flex-col">
    <div
      class="flex h-15-screen items-start justify-between p-6 2xl:h-10-screen"
    >
      <page-title
        :title="$t('PRESETS.TITLE')"
        :description="
          isLendflowUser
            ? $t('PRESETS.DESCRIPTION_LENDFLOW')
            : $t('PRESETS.DESCRIPTION_CLIENT')
        "
      />
      <div class="flex space-x-4">
        <search-input
          v-model="filters.search"
          no-margin
          no-padding
          trim-value
        />

        <primary-button
          data-cy="button-add-preset"
          @click.prevent="showPresetModal = true"
        >
          {{ $t("PRESETS.ADD_PRESET") }}
        </primary-button>
      </div>
    </div>

    <lf-table
      class="freeze-first freeze-border-first px-6 pb-4"
      data-cy="presets-table"
      header-sticky
      :class="tableClassList"
      :columns="presetsColumns"
      :pagination-absolute="false"
      :is-table-loading="loading"
      :data="all?.data ?? []"
      :metadata="all?.meta"
      @change-page="changePage($event)"
      @change-items-per-page="changePerPage($event)"
    />

    <preset-modal
      v-if="showPresetModal"
      :close="() => (showPresetModal = false)"
    />
  </div>
</template>

<script lang="ts">
import { type PropType, computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import compact from "lodash/compact";

import { DEFAULT_PER_PAGE } from "@/helpers/constants";
import { commaSeparatedStringFormatter } from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { usePermissionsPresetsQuery } from "@/hooks/queries/usePermissionsPresets";

import type { IDealsColumn } from "@/models/applications";
import type { IClient } from "@/models/clients";
import type { PermissionsPresetFilter } from "@/models/permissionsPreset";
import type { IWorkflow } from "@/models/workflows";
</script>

<script setup lang="ts">
import PresetActionMenu from "@/components/presets/PresetActionMenu.vue";
import PresetCategoriesColumn from "@/components/presets/PresetCategoriesColumn.vue";
import PresetNameColumn from "@/components/presets/PresetNameColumn.vue";
import PresetModal from "@/components/presets/presetModal/PresetModal.vue";
import PageTitle from "@/components/ui/PageTitle.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import LfTable from "@/components/ui/table/LfTable.vue";

defineProps({
  tableClassList: {
    type: [String, Object] as PropType<string | Record<string, boolean>>,
    default: "h-85-screen 2xl:h-90-screen",
  },
});

const { t } = useI18n();
const { isLendflowUser } = useAuth();

const filters = ref<
  Omit<
    PermissionsPresetFilter,
    "assigned_workflow_template_ids" | "assigned_client_ids"
  >
>({
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  search: "",
});

const { data: all, isLoading: loading } = usePermissionsPresetsQuery(filters);

const showPresetModal = ref(false);

const changePage = (page: number) => {
  filters.value.page = page;
};

const changePerPage = (perPage: number) => {
  filters.value.per_page = perPage;
};

const presetsColumns = computed(
  (): Array<IDealsColumn> =>
    compact([
      {
        key: "name",
        label: t("PRESETS.PRESET_NAME"),
        component: PresetNameColumn,
      },
      {
        key: "",
        label: t("PRESETS.CATEGORIES"),
        component: PresetCategoriesColumn,
      },
      {
        key: "assigned_workflow_templates",
        label: t("COMMON.TEMPLATE", 2),
        formatter: (templates: Array<IWorkflow>) =>
          commaSeparatedStringFormatter(templates, "name"),
      },
      isLendflowUser && {
        key: "assigned_clients",
        label: t("PRESETS.CLIENTS"),
        formatter: (clients: Array<IClient>) =>
          commaSeparatedStringFormatter(clients, "name"),
      },
      {
        key: "actions",
        label: "",
        component: PresetActionMenu,
      },
    ])
);
</script>
